import { useEffect, useState, useMemo, useCallback } from "react";
import { apiFetch } from "./api";
import { FaSitemap, FaDumbbell, FaCheck, FaPlus, FaTimes, FaWrench, FaSync, FaGem, FaArrowRight, FaDotCircle, FaStickyNote, FaBan } from "react-icons/fa";

const ACTIVITY_STYLES = {
  athlete_rsvp: { icon: FaCheck, accent: "#16a34a", label: "Athlete RSVP" },
  coach_rsvp: { icon: FaCheck, accent: "#2563eb", label: "Coach RSVP" },
  practice_created: { icon: FaPlus, accent: "#0891b2", label: "Practice" },
  practice_deleted: { icon: FaTimes, accent: "#dc2626", label: "Practice Deleted" },
  practice_note_added: { icon: FaStickyNote, accent: "#0891b2", label: "Practice Note" },
  practice_cancelled: { icon: FaBan, accent: "#dc2626", label: "Practice Cancelled" },
  maintenance: { icon: FaWrench, accent: "#f59e0b", label: "Maintenance" },
  maintenance_update: { icon: FaSync, accent: "#f97316", label: "Maintenance Update" },
  asset_status_change: { icon: FaGem, accent: "#8b5cf6", label: "Asset Status" },
  lineup_published: { icon: FaSitemap, accent: "#122f53", label: "Lineups" },
  user_invited: { icon: FaArrowRight, accent: "#06b6d4", label: "Invite Sent" },
  user_registered: { icon: FaCheck, accent: "#10b981", label: "Registration" },
  oyo_workout_logged: { icon: FaDumbbell, accent: "#06b6d4", label: "OYO Workout" },
  default: { icon: FaDotCircle, accent: "#64748b", label: "Activity" },
};

export default function RecentRsvpsTile({ team, limit = 20, pollMs = 30000, className = "", onMaintenanceClick, onPracticeClick, onOyoWorkoutClick, onLineupClick, userRole = "coach" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const pageSize = 5;

  const load = useCallback(async () => {
    if (!team) {
      setItems([]);
      setError("");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      // Use different endpoints based on user role
      const endpoint = userRole === "athlete"
        ? `/activity/athlete/recent?team=${encodeURIComponent(team)}&limit=${limit}`
        : `/rsvps/recent?team=${encodeURIComponent(team)}&limit=${limit}`;

      const r = await apiFetch(endpoint);
      setItems(Array.isArray(r.items) ? r.items : []);
    } catch (e) {
      console.error("load RSVPs failed", e);
      setItems([]);
      setError(e?.message || "Failed to load RSVPs");
    } finally {
      setLoading(false);
    }
  }, [team, limit, userRole]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!pollMs || !team) return;
    const id = setInterval(load, pollMs);
    return () => clearInterval(id);
  }, [team, pollMs, load]);

  const list = useMemo(() => items.slice(0, limit), [items, limit]);
  useEffect(() => {
    setPageIndex(0);
  }, [team, limit]);
  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(list.length / pageSize) - 1);
    if (pageIndex > maxPage) {
      setPageIndex(maxPage);
    }
  }, [list.length, pageIndex, pageSize]);

  const visibleItems = list.slice(pageIndex * pageSize, pageIndex * pageSize + pageSize);
  const canPrev = pageIndex > 0;
  const canNext = (pageIndex + 1) * pageSize < list.length;
  const classes = ["card rsvp-card", className].filter(Boolean).join(" ");

  function renderActivityItem(it, i) {
    switch (it.type) {
      case "athlete_rsvp":
        return renderAthleteRsvpActivity(it, i);
      case "coach_rsvp":
        return renderCoachRsvpActivity(it, i);
      case "practice_created":
        return renderPracticeCreatedActivity(it, i);
      case "practice_deleted":
        return renderPracticeDeletedActivity(it, i);
      case "practice_note_added":
        return renderPracticeNoteActivity(it, i);
      case "practice_cancelled":
        return renderPracticeCancelledActivity(it, i);
      case "maintenance":
        return renderMaintenanceActivity(it, i);
      case "maintenance_update":
        return renderMaintenanceUpdateActivity(it, i);
      case "asset_status_change":
        return renderAssetStatusActivity(it, i);
      case "lineup_published":
        return renderLineupPublishedActivity(it, i);
      case "user_invited":
        return renderInviteActivity(it, i);
      case "user_registered":
        return renderRegistrationActivity(it, i);
      case "oyo_workout_logged":
        return renderOyoWorkoutActivity(it, i);
      default:
        return renderBaseRow({
          key: i,
          type: it.type || "default",
          action: ACTIVITY_STYLES[it.type]?.label || "Activity",
          actor: it.userName || it.user || "",
          detail: it.subtitle || "",
          time: formatActionTimestamp(it),
        });
    }
  }

  function renderAthleteRsvpActivity(it, i) {
    const practiceDate = it.date ? formatPracticeDate(it.date, it.startTime || it.time) : "";
    const status = formatRsvpStatus(it.status);
    const isNotAvailable = it.status === "Not Available";
    const detail = (
      <span>
        {practiceDate}
        {practiceDate ? " | " : ""}
        <span className={status.className} style={status.style}>{status.label}</span>
      </span>
    );

    return renderBaseRow({
      key: i,
      type: "athlete_rsvp",
      action: "Athlete RSVP",
      actor: it.athlete,
      detail,
      time: formatActionTimestamp(it),
      icon: isNotAvailable ? FaTimes : undefined,
      accent: isNotAvailable ? "#dc2626" : undefined,
    });
  }

  function renderCoachRsvpActivity(it, i) {
    const practiceDate = it.date ? formatPracticeDate(it.date, it.startTime || it.time) : "";
    const status = formatRsvpStatus(it.status);
    const detail = (
      <span>
        {practiceDate}
        {practiceDate ? " | " : ""}
        <span className={status.className} style={status.style}>{status.label}</span>
      </span>
    );

    return renderBaseRow({
      key: i,
      type: "coach_rsvp",
      action: "Coach RSVP",
      actor: it.athlete,
      detail,
      time: formatActionTimestamp(it),
    });
  }

  function renderPracticeCreatedActivity(it, i) {
    return renderBaseRow({
      key: i,
      type: "practice_created",
      action: "Practice Created",
      actor: it.createdBy || "Coach",
      time: formatActionTimestamp(it),
      onClick:
        onPracticeClick && it.practiceId
          ? () => onPracticeClick(it.practiceId, it.date, it.startTime || it.time, it.squadName, it.endTime)
          : undefined,
    });
  }

  function renderPracticeDeletedActivity(it, i) {
    const practiceDate = it.date ? formatPracticeDate(it.date, it.startTime || it.time) : "";

    return renderBaseRow({
      key: i,
      type: "practice_deleted",
      action: "Practice Deleted",
      actor: it.deletedBy || "Coach",
      detail: practiceDate,
      time: formatActionTimestamp(it),
    });
  }

  function renderPracticeNoteActivity(it, i) {
    const practiceDate = it.date ? formatPracticeDate(it.date, it.startTime || it.time) : "";

    return renderBaseRow({
      key: i,
      type: "practice_note_added",
      action: "Practice Note",
      actor: it.addedBy || "Coach",
      detail: it.notePreview || practiceDate,
      time: formatActionTimestamp(it),
      onClick: onPracticeClick && it.practiceId
        ? () => onPracticeClick(it.practiceId, it.date, it.startTime || it.time, it.squadName, it.endTime)
        : undefined,
    });
  }

  function renderPracticeCancelledActivity(it, i) {
    const practiceDate = it.date ? formatPracticeDate(it.date, it.startTime || it.time) : "";

    return renderBaseRow({
      key: i,
      type: "practice_cancelled",
      action: "Practice Cancelled",
      actor: it.cancelledBy || "Coach",
      detail: practiceDate,
      time: formatActionTimestamp(it),
      onClick: onPracticeClick && it.practiceId
        ? () => onPracticeClick(it.practiceId, it.date, it.startTime || it.time, it.squadName, it.endTime)
        : undefined,
    });
  }

  function renderMaintenanceActivity(it, i) {
    const isClickable = !!(onMaintenanceClick && it.taskId);
    const equipmentType = it.equipmentType ? it.equipmentType.charAt(0).toUpperCase() + it.equipmentType.slice(1) : "";

    return renderBaseRow({
      key: i,
      type: "maintenance",
      action: "Maintenance",
      actor: it.submittedBy || "Submitted",
      detail: equipmentType,
      time: formatActionTimestamp(it),
      onClick: isClickable ? () => onMaintenanceClick(it.taskId) : undefined,
    });
  }

  function renderLineupPublishedActivity(it, i) {
    const practiceDate = it.date ? formatPracticeDate(it.date, it.startTime || it.time) : "";

    return renderBaseRow({
      key: i,
      type: "lineup_published",
      action: "Lineups Published",
      actor: it.publishedBy || "Coach",
      detail: practiceDate,
      time: formatActionTimestamp(it),
      onClick: onLineupClick && it.date ? () => onLineupClick(it.date) : undefined,
    });
  }

  function renderMaintenanceUpdateActivity(it, i) {
    return renderBaseRow({
      key: i,
      type: "maintenance_update",
      action: "Maintenance Update",
      actor: it.updatedBy || "Coach",
      time: formatActionTimestamp(it),
      onClick: it.taskId && onMaintenanceClick ? () => onMaintenanceClick(it.taskId) : undefined,
    });
  }

  function renderAssetStatusActivity(it, i) {
    return renderBaseRow({
      key: i,
      type: "asset_status_change",
      action: "Asset Status",
      actor: it.updatedBy || "Coach",
      time: formatActionTimestamp(it),
    });
  }

  function renderInviteActivity(it, i) {
    return renderBaseRow({
      key: i,
      type: "user_invited",
      action: "Invite Sent",
      actor: it.invitedBy || "Coach",
      time: formatActionTimestamp(it),
    });
  }

  function renderRegistrationActivity(it, i) {
    return renderBaseRow({
      key: i,
      type: "user_registered",
      action: "Registration",
      actor: it.userName || it.userEmail,
      time: formatActionTimestamp(it),
    });
  }

  function renderOyoWorkoutActivity(it, i) {
    const workoutId = it.workoutId ?? it.workout_id ?? it.id ?? null;

    return renderBaseRow({
      key: i,
      type: "oyo_workout_logged",
      action: "OYO Workout",
      actor: it.athleteName || "Athlete",
      time: formatActionTimestamp(it),
      onClick: onOyoWorkoutClick && workoutId
        ? () => onOyoWorkoutClick(workoutId)
        : undefined,
    });
  }

  function renderBaseRow({ key, type, action, actor, detail, onClick, time, icon, accent }) {
    const style = ACTIVITY_STYLES[type] || ACTIVITY_STYLES.default;
    const IconComponent = icon || style.icon;
    const rowClasses = ["activity-item", "rsvp-row", "activity-row"];
    if (onClick) rowClasses.push("clickable");
    return (
      <div key={key} className={rowClasses.join(" ")} onClick={onClick}>
        <div
          className="activity-item-icon"
          style={{ background: accent || style.accent, border: style.border ? `2px solid ${style.border}` : undefined, flexShrink: 0 }}
        >
          <IconComponent aria-label={style.label} />
        </div>
        <div className="activity-item-content">
          <div className="activity-item-header">
            <span className="rsvp-name">{action || style.label}</span>
            {time && <span className="help activity-time">{time}</span>}
          </div>
          {(actor || detail) && (
            <div className="activity-item-user">
              {actor && <span>{actor}</span>}
              {actor && detail && <span> • </span>}
              {detail && <span>{detail}</span>}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={classes}>
      <div className="recent-activity-header">
        {list.length > pageSize && (
          <div className="activity-nav">
            <button
              className="activity-nav-btn"
              onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
              disabled={!canPrev}
              aria-label="Previous activity"
            >
              ‹
            </button>
            <button
              className="activity-nav-btn"
              onClick={() => setPageIndex((p) => Math.min(p + 1, Math.ceil(list.length / pageSize) - 1))}
              disabled={!canNext}
              aria-label="Next activity"
            >
              ›
            </button>
          </div>
        )}
      </div>

      <div className="recent-activity-list">
        {!team && <div className="help">Add a team to your coach profile to see recent updates.</div>}
        {team && list.length === 0 && !loading && !error && <div className="help">No recent updates.</div>}
        {team && visibleItems.map((it, i) => renderActivityItem(it, i))}
      </div>

      {list.length > pageSize && (
        <div className="activity-indicators">
          {Array.from({ length: Math.ceil(list.length / pageSize) }).map((_, idx) => (
            <div
              key={idx}
              className={`activity-indicator ${pageIndex === idx ? "active" : ""}`}
              onClick={() => setPageIndex(idx)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function formatPracticeDate(dateStr, startTime) {
  if (!dateStr) return "";
  try {
    const d = new Date(`${dateStr}T00:00:00`);
    if (isNaN(d.getTime())) return dateStr;

    const datePart = d.toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric"
    });

    if (startTime) {
      const [hours, minutes] = startTime.split(":");
      const h = parseInt(hours, 10);
      const period = h >= 12 ? "PM" : "AM";
      const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
      const m = parseInt(minutes || "0", 10);
      return `${datePart} ${hour12}:${String(m).padStart(2, "0")} ${period}`;
    }

    return datePart;
  } catch {
    return dateStr;
  }
}

function formatRsvpStatus(status) {
  const s = String(status || "");
  if (s === "Available") return { label: "Available", className: "", style: {} };
  if (s === "Not Available") return { label: "Not Available", className: "", style: { color: "var(--danger)" } };
  return { label: status || "No Response", className: "", style: {} };
}

function formatActionTimestamp(item) {
  const dateStr = item.when || item.createdAt || item.created_at || item.performedOn || item.date || item.updatedAt || item.updated_at || item.submittedAt || item.submitted_at || item.invitedAt || item.invited_at || item.publishedAt || item.published_at || item.timestamp;

  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}
